<template>
  <div class="contact-info-search">
    <div class="contact-info-search__form form">
      <div class="form__grid">

        <!-- City -->
        <LoadSpinner v-if="loadingCities"/>
        <InputWrap
          v-else
          :label="translate('Paikkakunta')"
          :wrap-label="false"
          v-slot="{id}"
          class="form__col"
        >
          <InputSelect
            :id="id"
            :options="optionsCity"
            v-model="cityUuid"
            open-direction="bottom"
            simple-value
          />
        </InputWrap>

        <!-- Housing locality -->
        <template v-if="city">
          <LoadSpinner v-if="loadingHousingLocalities"/>
          <InputWrap
            v-else
            :label="translate('Osoite')"
            :wrap-label="false"
            v-slot="{id}"
            class="form__col"
          >
            <InputSelect
              :id="id"
              :options="optionsHousingLocality"
              v-model="housingLocalityUuid"
              open-direction="bottom"
              simple-value
            />
          </InputWrap>
        </template>

        <div
          v-if="!housingLocality"
          class="contact-info-search__spacer"
        />
      </div>
    </div>

    <div
      v-if="housingLocality"
      class="contact-info-search__results"
    >
      <div class="contact-info-search__results-header">
        <div class="contact-info-search__results-title">
          {{housingLocality.mpc_katuosoite}}, {{housingLocality.mpc_postinumero}} {{city.mpc_name}}
        </div>
        <button
          class="btn contact-info-search__print"
          @click="print"
        >
          <PrintIcon class="btn__icon btn__icon--left contact-info-search__print-icon"/>
          {{translate('Tulosta sivu')}}
        </button>
      </div>

      <div class="contact-info-search__cards">
        <div
          class="contact-info-search__card-column"
          v-for="card in cards"
          :key="`${card.title}-${card.email}`"
        >
          <div class="contact-info-search__card">
            <div class="contact-info-search__icon"/>
            <div class="contact-info-search__card-title">
              {{translate(card.title)}}
            </div>
            <div class="contact-info-search__card-line">
              {{card.name}}
            </div>
            <div class="contact-info-search__card-line"
              v-html="addTelLink(card.phone)"
            />

            <component
              :is="card.email ? 'a' : 'div'"
              :href="`mailto:${card.email}`"
              class="contact-info-search__card-line"
              :class="{'contact-info-search__card-line--link': card.email}"
            >
              {{card.email}}
            </component>

            <div class="contact-info-search__card-line">
              {{card.address || '-'}}
            </div>

            <div
              v-if="card.availability"
              class="contact-info-search__card-line"
            >
              {{card.availability}}
            </div>

            <div
              v-if="card.info"
              class="contact-info-search__card-line"
            >
              {{card.info}}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';

  import globals from '../lib/globals';
  import {removeSpaces} from '../lib/format';
  import utils from './mixins/utils';
  import InputSelect from './components/InputSelect';
  import InputWrap from './components/InputWrap';
  import LoadSpinner from './components/LoadSpinner';
  import PrintIcon from './icons/PrintIcon.vue';

  function createCard(title, fields) {
    const empty = Object.entries(fields).every(([, value]) => !value);
    return empty ? null : {
      title,
      ...fields,
    };
  }

  export default {
    mixins: [utils.translate],
    components: {
    LoadSpinner,
    InputSelect,
    InputWrap,
    PrintIcon
},
    data: () => ({
      loadingCities: false,
      loadingHousingLocalities: false,
      cities: null,
      housingLocalities: null,
      cityUuid: null,
      housingLocalityUuid: null,
    }),
    computed: {
      optionsHousingLocality() {
        return this.housingLocalities.map(housingLocality => ({
          label: housingLocality.mpc_katuosoite,
          value: housingLocality.mpc_kohdeid,
        })).sort(this.sortOptions);
      },
      optionsCity() {
        return this.cities.map(city => ({
          label: city.mpc_name,
          value: city.mpc_kuntaid,
        })).sort(this.sortOptions);
      },
      city() {
        return this.cities
          ? this.cities.find(city => city.mpc_kuntaid === this.cityUuid)
          : null;
      },
      housingLocality() {
        return this.housingLocalities
          ? this.housingLocalities.find(housingLocality => housingLocality.mpc_kohdeid === this.housingLocalityUuid)
          : null;
      },
      cards() {
        const hl = this.housingLocality;
        const st = this.housingLocality.sales_territory;
        if (!hl) {
          return null;
        }

        return [
          createCard('Isännöitsijä', {
            name: hl.mpc_isannoitsijannimi,
            phone: hl.mpc_isannoitsijanpuhelin,
            email: hl.mpc_isannoitsijansahkoposti,
            address: hl.mpc_isannoitsijanosoite,
            availability: hl.ava_isannoitsijatavoitettavissa,
            info: hl.ava_isannoitsijanlisatiedot,
          }),
          createCard('Muuttotarkastaja', {
            name: hl.mpc_muuttotarkastanjanimi,
            phone: hl.mpc_muuttotarkastajanpuhelin,
            email: hl.mpc_muuttotarkastajansahkoposti,
            address: hl.mpc_muuttotarkastanjaosoite,
            availability: hl.ava_muuttotarkastajatavoitettavissa,
            info: hl.ava_muuttotarkastajanlisatiedot,
          }),
          createCard('Kiinteistösihteeri', {
            name: hl.mpc_kiinteistosihteerinimi,
            phone: hl.mpc_kiinteistosihteeripuhelin,
            email: hl.mpc_kiinteistosihteerisahkopostiosoite,
            address: hl.mpc_kiinteistosihteeriosoite,
            availability: hl.ava_kiinteistosihteeritavoitettavissa,
            info: hl.ava_kiinteistosihteerinlisatiedot,
          }),
          createCard('Vastikevalvonta', {
            name: hl.mpc_vastikevalvojannimi,
            phone: hl.mpc_vastikevalvojanpuhelin,
            email: hl.mpc_vastikevalvojansahkoposti,
            address: hl.mpc_vastikevalvojanosoite,
            availability: hl.ava_vastikevalvonnanaukioloajat,
            info: hl.ava_vastikevalvonnanlisatiedot,
          }),
          createCard('Huoltoyhtiö', {
            name: hl.mpc_huoloyhtionnimi,
            phone: hl.mpc_huoltoyhtionpuhelin,
            email: hl.mpc_huoltoyhtionsahkopostiosoite,
            address: hl.mpc_huoltoyhtionosoite,
            availability: hl.ava_huoltoyhtionaukioloajat,
            info: hl.ava_huoltoyhtionlisatiedot,
          }),
          createCard('Asunnon avainten nouto ja palautus', {
            name: hl.ava_avainhallinta,
            phone: hl.ava_avainhallinnanpuhelin,
            email: hl.ava_avainhallinnansahkoposti,
            address: hl.ava_avainhallinnanosoite,
            availability: hl.ava_avainhallinnanaukioloajat,
            info: hl.ava_avainhallinnanlisatiedot,
          }),
          createCard('Autopaikkojen avainten nouto ja palautus', {
            name: hl.ava_autopaikkojenavainhallinta,
            phone: hl.ava_autopaikkojenavainhallinnanpuhelin,
            email: hl.ava_autopaikkojenavainhallinnansahkoposti,
            address: hl.ava_autopaikkojenavainhallinnanosoite,
            availability: hl.ava_autopaikkojenavainhallinnanaukioloajat,
            info: hl.ava_autopaikkojenavainhallinnanlisatiedot,
          }),
          st ? createCard('Asuntojen myynti', {
            name: st.ava_myyntialue,
            phone: st.mpc_puhelinnumero,
            email: st.ava_myynninsahkoposti,
            address: `${st.mpc_kayntiosoite}, ${st.mpc_postitoimipaikka} ${st.mpc_postinumero}`,
            availability: st.ava_myynninaukioloajat,
            info: st.ava_myynninlisatiedot,
          }) : null,
          createCard('Isännöintitoimisto', {
            name: hl.ava_isannoinnintoimipiste,
            phone: hl.ava_isaninnointoimipisteenpuhelin,
            email: hl.ava_isannoinintoimipisteensahkoposti,
            address: hl.ava_isanninnointoimipisteenosoite,
            availability: hl.ava_isannoinnintoimipisteenaukioloajat,
            info: hl.ava_isannoinnintoimipisteenlisatiedot,
          }),
        ].filter(card => card !== null);
      }
    },
    watch: {
      // When a city is selected, get housing localities
      cityUuid() {
        if (this.cityUuid) {
          this.getHousingLocalities();
        }
      }
    },
    methods: {
      sortOptions(a, b) {
        a = a.label.toLowerCase();
        b = b.label.toLowerCase();
        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }
        return 0;
      },
      async getCities() {
        this.loadingCities = true;
        const url = `${globals.urlIntegrationApi}/public/cities-all`;
        const response = await axios.get(url);

        this.cities = response.data;

        this.loadingCities = false;
      },
      async getHousingLocalities() {
        this.loadingHousingLocalities = true;
        const url = `${globals.urlIntegrationApi}/public/housinglocalities-by-city/${this.cityUuid}`;
        const response = await axios.get(url);

        this.housingLocalities = response.data;

        this.loadingHousingLocalities = false;
      },
      print() {
        window.print();
      },
      removeSpaces,
      addTelLink(phone) {
        if(!phone) return '-';
        return `<a href="tel:${removeSpaces(phone)}">${phone}</a>`;
      }
    },
    beforeMount() {
      this.getCities();
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  .contact-info-search {
  }

  .contact-info-search__form {
    max-width: r(432);

    @media print {
      display: none;
    }
  }

  // This is needed to make sure that InputSelect dropdowns have enough room below to expand.
  .contact-info-search__spacer {
    min-height: r(300);
  }

  .contact-info-search__results {
    padding: r(24) r(16);
    margin-top: r(48);
    background-color: #f2f2f2;

    @media print {
      background-color: transparent;
      padding: 0;
    }
  }

  .contact-info-search__results-header {
    margin-bottom: r(32);

    @include media-breakpoint-up(md) {
      display: flex;
      align-items: flex-start;
    }
  }

  .contact-info-search__results-title {
    @include font-h-22;
    flex-grow: 1;
    margin-bottom: r(16);

    @include media-breakpoint-up(md) {
      margin-right: r(16);
      margin-bottom: 0;
    }
  }

  .contact-info-search__print {
    white-space: nowrap;

    @media print {
      display: none;
    }
  }

  .contact-info-search__print-icon {
    svg {
      @include square(r(28));
    }
  }

  .contact-info-search__cards {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    margin: 0 r(-4) r(-8);

    @media print {
      margin: r(-16) r(-16);
    }
  }

  .contact-info-search__card-column {
    display: flex;
    padding: 0 r(4) r(8);
    width: 100%;

    @include media-breakpoint-up(lg) {
      width: percentage(calc(1 / 2));
    }

    @media print {
      padding: r(16) r(16);
      width: percentage(calc(1 / 2));
    }
  }

  .contact-info-search__card {
    @include card;
    flex: 1 0 100%;
    position: relative;
    background-color: $color-white;
    padding: r(20) r(24) r(24) r(40);

    @media print {
      page-break-inside: avoid;
      box-shadow: none;
      border: 0;
      padding: 0;
    }
  }

  .contact-info-search__icon {
    @include square(r(16));
    position: absolute;
    left: r(16);
    border-radius: r(8);
    background-color: $color-blue-deep;

    @media print {
      display: none;
    }
  }

  .contact-info-search__card-title {
    @include font-label-16;
    margin-bottom: r(8);
  }

  .contact-info-search__card-line {
    @include font-body-12;

    & + & {
      margin-top: r(8);
    }

    &--link {
      text-decoration: underline;
    }
  }
</style>
